import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, CssBaseline, Typography, Grid, Tab, Tabs, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, IconButton }
    from "@mui/material";
import Dashboard from "../components/shared/dashboard/dashboard";
import MetaDecorator from "../services/meta_decorator";
import Newheader from "../components/shared/newheader";
import Footer from "../components/shared/footer";
import AccessLocalStorage from "../services/localStorage";
import InactivityMeter from "../components/shared/inactivitymeter";
import { getApiUrl } from "../services/api_service";
import { ConvertText } from "../services/utility";
import { dashboardText, epilepsyHeading2, footerContainer, mainGrid, paddingboxeven, pcolor, rcolor, orangecolor, darkcolor, lightcolor, primaryColor, select, selectUnderLined, whitecolor, lightpinkcolor, brownColor, redHeading } from "../style2";
import BackToTop from "../components/shared/scrollTop";
import { AISummaryList } from "../components/shared/aisummarylist";
import { useParams } from "react-router-dom";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from "docx";
import dayjs from "dayjs";
import HealthDiary from "../components/patient_profile/health_diary";
import SeizureDiary from "../components/patient_profile/seizure_diary";
import SleepDiary from "../components/patient_profile/sleep_diary";
import { FormsList } from "../components/patient_profile/forms_list";
import { useAuth } from "../contexts/AuthContext";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const EMC = {
    teal: "#4EBFB0",
    tealLight: "rgba(78,191,176,0.12)",
    navy: pcolor,
    navyLight: "rgba(17,92,113,0.10)",
    coral: rcolor,
    coralLight: lightpinkcolor,
    gold: orangecolor,
    goldLight: "rgba(255,145,77,0.12)",
    green: "#3DAA6E",
    greenLight: "rgba(61,170,110,0.12)",
    bg: lightcolor,
    cardBg: whitecolor,
    border: "rgba(17,92,113,0.12)",
    textPrimary: pcolor,
    textSecondary: darkcolor,
    font: "'Plus Jakarta Sans', sans-serif",
};

// ─── Field Colour Mapping ───────────────────────────────────────────────────────────
const FIELD_COLORS = {
    shared_concerns: { color: EMC.teal, bg: EMC.tealLight },
    risk_flags: { color: EMC.coral, bg: EMC.coralLight },
    parent_concerns: { color: EMC.navy, bg: EMC.navyLight },
    teacher_concerns: { color: EMC.gold, bg: EMC.goldLight },
    strengths: { color: EMC.green, bg: EMC.greenLight },
    recommendations: { color: EMC.teal, bg: EMC.tealLight },
    concerns: { color: EMC.coral, bg: EMC.coralLight },
    observations: { color: EMC.navy, bg: EMC.navyLight },
    achievements: { color: EMC.green, bg: EMC.greenLight },
};

const DEFAULT_COLORS = [
    { color: EMC.navy, bg: EMC.navyLight },
    { color: EMC.teal, bg: EMC.tealLight },
    { color: EMC.gold, bg: EMC.goldLight },
    { color: primaryColor, bg: EMC.greenLight },
];

const getFieldColor = (key) => {
    const lk = key.toLowerCase();
    for (const [pattern, colors] of Object.entries(FIELD_COLORS)) {
        if (lk.includes(pattern)) return colors;
    }
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
    return DEFAULT_COLORS[Math.abs(hash) % DEFAULT_COLORS.length];
};

const isAlertField = (key) => {
    const lk = key.toLowerCase();
    return lk.includes("discrepancy") || lk.includes("variability") || lk.includes("risk_flag");
};

const humanize = (key) =>
    key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, (c) => c.toUpperCase());

const valueToEditString = (v) => {
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "object" && v !== null) {
        if (v.value !== undefined) return String(v.value);
        return JSON.stringify(v);
    }
    return String(v ?? "");
};

const parseOutput = (output) => {
    if (!output) return {};
    try { return typeof output === "string" ? JSON.parse(output) : output; }
    catch { return { content: String(output) }; }
};

const parseSchema = (outputschema) => {
    if (!outputschema) return {};
    try { return typeof outputschema === "string" ? JSON.parse(outputschema) : outputschema; }
    catch { return {}; }
};

const getOrderedKeys = (schema, parsed) => {
    const schemaKeys = Object.keys(parseSchema(schema));
    const parsedKeys = Object.keys(parseOutput(parsed));
    if (schemaKeys.length > 0) {
        const ordered = schemaKeys.filter(k => parsedKeys.includes(k));
        const extra = parsedKeys.filter(k => !ordered.includes(k));
        return [...ordered, ...extra];
    }
    return parsedKeys;
};

// ─── FullField ──────────────────────────────────────────────────────────────────
function FullField({ fieldKey, value, isFirst }) {
    const hasValue = Array.isArray(value)
        ? value.length > 0
        : value !== null && value !== undefined && String(value).trim() !== "";
    if (!hasValue) return null;

    const colors = getFieldColor(fieldKey);

    if (isAlertField(fieldKey)) {
        const text = Array.isArray(value) ? value.join(" · ") : String(value);
        const isRisk = fieldKey.toLowerCase().includes("risk");
        return (
            <Box sx={{
                background: isRisk ? EMC.coralLight : EMC.goldLight,
                border: `1px solid ${isRisk ? EMC.coral : EMC.gold}`,
                borderRadius: "8px", padding: "12px 16px",
                mt: isFirst ? 0 : 2,
            }}>
                <Typography sx={{
                    color: isRisk ? EMC.coral : EMC.gold,
                    textTransform: "uppercase", mb: 1, mt:1
                }}>
                    {humanize(fieldKey)}
                </Typography>
                <Typography sx={{ fontFamily: EMC.font, color: EMC.textPrimary }}>
                    {text}
                </Typography>
            </Box>
        );
    }

    if (Array.isArray(value)) {
        return (
            <Box sx={{ mt: isFirst ? 0 : 2 }}>
                <Typography sx={{
                    fontWeight: 700,
                    color: primaryColor, textTransform: "uppercase",
                     mb: 1, mt:2
                }}>
                    {humanize(fieldKey)}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.7}>
                    {value.map((item, i) => (
                        <Box key={i} component="span" sx={{
                            background: colors.bg, color: colors.color,
                            border: `1px solid ${colors.color}33`,
                            borderRadius: "20px", padding: "4px 13px",
                            
                            display: "inline-block",
                        }}>
                            {String(item)}
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    if (typeof value === "object" && value !== null) {
        return (
            <Box sx={{ mt: isFirst ? 0 : 2 }}>
                <Typography sx={{
                     fontWeight: 700,
                    color: primaryColor, textTransform: "uppercase",
                    letterSpacing: "0.06em", mb: 1, mt:2,
                }}>
                    {humanize(fieldKey)}
                </Typography>
                <Box sx={{ background: colors.bg, borderRadius: "6px", padding: "10px 14px", border: `1px solid ${colors.color}22` }}>
                    {Object.entries(value).map(([k, v]) => (
                        <Typography key={k} sx={{ fontFamily: EMC.font, fontSize: "0.86rem", color: EMC.textPrimary, lineHeight: 1.7 }}>
                            <strong>{humanize(k)}:</strong> {String(v)}
                        </Typography>
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: isFirst ? 0 : 2 }}>
            <Typography sx={{
                fontFamily: EMC.font,  fontWeight: 700,
                color: primaryColor, textTransform: "uppercase",
                letterSpacing: "0.06em", mb: 1, mt:2
            }}>
                {humanize(fieldKey)}
            </Typography>
            <Typography sx={{ fontFamily: EMC.font, color: EMC.textPrimary }}>
                {typeof value === "string" ? value : String(value)}
            </Typography>
        </Box>
    );
}

// ─── DOCX Download Helper ───────────────────────────────────────────────────────────
function buildDocxValue(value) {
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object" && value !== null)
        return Object.entries(value).map(([k, v]) => `${humanize(k)}: ${v}`).join("\n");
    return String(value ?? "");
}

async function downloadSummaryDocx(report, parsed, allKeys) {
    const ls = new AccessLocalStorage();
    const cp = ls.getCurrentPatient();
    const patient = cp?.user ?? {};
    const fullName = [patient.firstName, patient.surname].filter(Boolean).join(" ") || "Unknown";
    const dob = patient.dob ? dayjs(patient.dob) : null;
    const ageYears = dob ? dayjs().diff(dob, "year") : null;
    const ageMonths = dob ? dayjs().diff(dob, "month") % 12 : null;
    const ageStr = dob
        ? ageYears > 0 ? `${ageYears}y ${ageMonths}m` : `${dayjs().diff(dob, "month")}m`
        : "Unknown";
    const nhsNumber = patient.nhsNumber ?? "Unknown";

    const titlePara = new Paragraph({
        children: [new TextRun({ text: "Summary Report", bold: true, size: 36, color: pcolor.replace("#", "") })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
    });

    const datePara = new Paragraph({
        children: [new TextRun({ text: dayjs(report.createdAt).format("DD MMMM YYYY"), size: 20, color: darkcolor.replace("#", "") })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
    });

    const patientHeading = new Paragraph({
        children: [new TextRun({ text: "Patient Information", bold: true, size: 26, color: pcolor.replace("#", "") })],
        spacing: { before: 200, after: 150 },
    });

    const divider = new Paragraph({
        border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: "CCCCCC" } },
        spacing: { after: 200 },
    });

    const patientRows = [
        ["Patient Name", fullName],
        ["Age", ageStr],
        ["NHS Number", nhsNumber],
        ["Pathway", report.pathwayName ?? "—"],
        ["Organisation", report.organizationName ?? "—"],
    ].map(([label, val]) =>
        new Paragraph({
            children: [
                new TextRun({ text: `${label}: `, bold: true, size: 22 }),
                new TextRun({ text: val, size: 22 }),
            ],
            spacing: { after: 80 },
        })
    );

    const summaryHeading = new Paragraph({
        children: [new TextRun({ text: "Summary", bold: true, size: 26, color: pcolor.replace("#", "") })],
        spacing: { before: 300, after: 150 },
    });

    const summaryFields = allKeys.flatMap((key) => {
        const value = parsed[key];
        const hasValue = Array.isArray(value)
            ? value.length > 0
            : value !== null && value !== undefined && String(value).trim() !== "";
        if (!hasValue) return [];

        const labelPara = new Paragraph({
            children: [new TextRun({ text: humanize(key), bold: true, size: 22, color: darkcolor.replace("#", ""), allCaps: true })],
            spacing: { before: 200, after: 60 },
        });

        if (Array.isArray(value)) {
            const bullets = value.map((item) =>
                new Paragraph({
                    children: [new TextRun({ text: String(item), size: 22 })],
                    bullet: { level: 0 },
                    spacing: { after: 40 },
                })
            );
            return [labelPara, ...bullets];
        }

        const contentPara = new Paragraph({
            children: [new TextRun({ text: buildDocxValue(value), size: 22 })],
            spacing: { after: 80 },
        });
        return [labelPara, contentPara];
    });

    const doc = new Document({
        sections: [{
            properties: {},
            children: [titlePara, datePara, patientHeading, divider, ...patientRows, summaryHeading, divider, ...summaryFields],
        }],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `summary-${fullName.replace(/\s+/g, "-")}-${dayjs(report.createdAt).format("DD-MM-YYYY")}.docx`;
    a.click();
    URL.revokeObjectURL(url);
}

// ─── FullReportCard ──────────────────────────────────────────────────────────────
function FullReportCard({ report }) {
    const parsed = parseOutput(report.output);
    const allKeys = getOrderedKeys(report.outputschema, report.output);

    return (
        <Box sx={{
            background: EMC.cardBg, borderRadius: "14px",
            border: `1px solid ${EMC.border}`,
            boxShadow: "0 2px 12px rgba(17,92,113,0.07)", overflow: "hidden",
            mb: 3,
        }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}
                sx={{
                    background: whitecolor, borderBottom: `1px solid ${EMC.border}`,
                    padding: "16px 22px",
                }}
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ background: whitecolor, borderRadius: "8px", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={pcolor} strokeWidth="2">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                    </Box>
                    <Box>
                        <Typography sx={{  fontWeight: 700, fontSize: "0.95rem", color: pcolor }}> Ariva AI Summary</Typography>
                        <Typography sx={{  fontSize: "0.72rem", color: pcolor }}>
                            {dayjs(report.createdAt).format("DD/MM/YYYY")}
                            {report.pathwayName ? ` · ${report.pathwayName}` : ""}
                            {report.organizationName ? ` · ${report.organizationName}` : ""}
                        </Typography>
                    </Box>
                </Box>
                <Button
                    size="small"
                    onClick={() => downloadSummaryDocx(report, parsed, allKeys)}
                    sx={{
                        textTransform: "none", color: pcolor,
                        background: "rgba(255,255,255,0.15)",
                        padding: "5px 12px",
                        border: `1px solid ${pcolor}`, borderRadius:"20px",
                        "&:hover": { background: "rgba(255,255,255,0.25)" },
                    }}
                >
                     Download
                </Button>
            </Box>

            {/* Body — all fields, no truncation */}
            <Box sx={{ padding: "20px 24px" }}>
                {allKeys.map((key, i) => (
                    <FullField key={key} fieldKey={key} value={parsed[key]} isFirst={i === 0} />
                ))}
            </Box>
        </Box>
    );
}

// ─── HistoryRow ───────────────────────────────────────────────────────────────
function HistoryRow({ report, onView }) {
    const hasSummary = !!report.output;
    const hasTriage = report.triageDetails?.triageEnabled ;
    const hasRecommendation = !!report.triageDetails?.recommendationsEnabled;

    const tags = [];
    if (hasSummary) tags.push({ label: "Summary", color: EMC.teal, bg: EMC.tealLight });
    if (hasTriage) {
        const modified = report.triageDetails?.isModified;
        tags.push({ label: modified ? "Triage — modified" : "Triage", color: EMC.gold, bg: EMC.goldLight });
    }
    if (hasRecommendation) tags.push({ label: "Recommendation", color: pcolor, bg: EMC.navyLight });

    const components = [
        hasSummary && `Version ${report.versionNumber ?? 1}`,
        hasTriage && `Triage`,
        hasRecommendation && `Recommendation`,
    ].filter(Boolean).join(" · ");

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}
            sx={{
                borderBottom: `1px solid ${EMC.border}`, py: 1.5,
                "&:last-child": { borderBottom: "none" },
            }}
        >
            <Box flex={1} minWidth={0}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography sx={{ fontFamily: EMC.font, fontSize: "0.84rem", fontWeight: 600, color: EMC.textPrimary }}>
                        {dayjs(report.createdAt).format("DD/MM/YYYY [at] HH:mm")}
                    </Typography>
                </Box>
                {components && (
                    <Typography sx={{ fontFamily: EMC.font, fontSize: "0.75rem", color: EMC.textSecondary, mt: 0.3 }}>
                        {components}
                    </Typography>
                )}
                {tags.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.6} mt={0.6}>
                        {tags.map((tag, i) => (
                            <Box key={i} component="span" sx={{
                                fontFamily: EMC.font, fontSize: "0.7rem", fontWeight: 600,
                                background: tag.bg, color: tag.color,
                                border: `1px solid ${tag.color}33`,
                                borderRadius: "20px", padding: "2px 9px",
                            }}>
                                {tag.label}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
            <Button size="small" variant="outlined" onClick={() => onView(report)}
                sx={{
                    fontFamily: EMC.font, fontWeight: 600, fontSize: "0.78rem",
                    textTransform: "none", color: pcolor,
                    border: `1px solid ${pcolor}44`,
                    borderRadius: "8px", padding: "4px 14px",
                    flexShrink: 0,
                    "&:hover": { background: EMC.navyLight, border: `1px solid ${pcolor}` },
                }}
            >
                View
            </Button>
        </Box>
    );
}

// ─── TriageGridCard ───────────────────────────────────────────────────────────
function TriageGridCard({ fieldKey, value, accent, modifiedValue, isModifiedStatus }) {
    const mainVal = typeof value === "object" && value !== null && value.value
        ? value.value
        : typeof value === "string" ? value : null;
    const factors = typeof value === "object" && value !== null && Array.isArray(value.factors)
        ? value.factors : null;
    const displayText = mainVal ?? (typeof value === "string" ? value : JSON.stringify(value));

    const isChanged = isModifiedStatus && modifiedValue !== undefined && modifiedValue.trim() !== (displayText ?? "").trim();
    const isAgreed = isModifiedStatus && modifiedValue !== undefined && modifiedValue.trim() === (displayText ?? "").trim();

    return (
        <Box sx={{
            flex: 1, minWidth: 200,
            background: accent.bg, border: `1px solid ${accent.color}33`,
            borderRadius: "10px", padding: "14px 16px",
        }}>
            <Typography sx={{
                fontWeight:700,
                color: accent.color, textTransform: "uppercase", letterSpacing: "0.07em", mb: 1,
            }}>
                {humanize(fieldKey)}
            </Typography>
            {isModifiedStatus ? (
                <Box>
                    <Typography sx={{  fontSize: "0.68rem", color: darkcolor, mb: 0.2 }}>AI</Typography>
                    <Typography sx={{  color: EMC.textPrimary, textDecoration: isChanged ? "line-through" : "none", mb: 0.5 }}>
                        {displayText}
                    </Typography>
                    {isChanged && (
                        <Box>
                            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: pcolor, mb: 0.2 }}>↗ Changed to</Typography>
                            <Typography sx={{ color: pcolor }}>{modifiedValue}</Typography>
                        </Box>
                    )}
                    {isAgreed && (
                        <Typography sx={{  fontSize: "0.68rem", fontWeight: 700, color: primaryColor }}>✓ Agreed</Typography>
                    )}
                </Box>
            ) : (
                <>
                    <Typography sx={{  color: EMC.textPrimary, mb: factors ? 1 : 0 }}>
                        {displayText}
                    </Typography>
                    {factors && (
                        <Box display="flex" flexWrap="wrap" gap={0.6} mt={0.5}>
                            {factors.map((f, i) => (
                                <Box key={i} component="span" sx={{
                                    background: `${accent.color}18`, color: accent.color,
                                    borderRadius: "20px", padding: "2px 9px",
                                    fontWeight: 600,
                                }}>{f}</Box>
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}

const DISAGREE_REASONS = [
    "Important clinical context missed",
    "AI overestimated severity",
    "AI underestimated severity",
    "Wrong pathway suggested",
    "Parent and teacher reports conflict",
    "Free-text misinterpretation",
    "Safeguarding concern not recognised",
    "Clinical judgement overrides AI",
    "Other",
];

const MODIFY_REASONS = [
    "AI underestimated severity",
    "AI overestimated severity",
    "Wrong pathway suggested",
    "Important clinical context missed",
    "Parent and teacher reports conflict",
    "Free-text misinterpretation",
    "Safeguarding concern not recognised",
    "Clinical judgement overrides AI",
    "Other",
];

// ─── TriagePanel ──────────────────────────────────────────────────────────────
function TriagePanel({ report, onReload }) {
    const triage = report.triageDetails;

    const [agreeing, setAgreeing] = useState(false);
    const [agreeError, setAgreeError] = useState("");

    const [modifyOpen, setModifyOpen] = useState(false);
    const [modifyReason, setModifyReason] = useState("");
    const [modifyNotes, setModifyNotes] = useState("");
    const [modifying, setModifying] = useState(false);
    const [modifyError, setModifyError] = useState("");

    const [disagreeOpen, setDisagreeOpen] = useState(false);
    const [disagreeReason, setDisagreeReason] = useState("");
    const [disagreeNotes, setDisagreeNotes] = useState("");
    const [disagreeing, setDisagreeing] = useState(false);
    const [disagreeError, setDisagreeError] = useState("");

    const [modifyTriageFields, setModifyTriageFields] = useState({});
    const [disagreeTriageFields, setDisagreeTriageFields] = useState({});

    if (!triage || !triage.triageEnabled || !triage.triageResult) return null;

    const initTriageFields = () => {
        const p = parseOutput(triage.triageResult);
        const f = {};
        Object.entries(p).forEach(([k, v]) => { f[k] = valueToEditString(v); });
        return f;
    };

    const handleDisagreeConfirm = async () => {
        
        if (!disagreeReason) {
            setDisagreeError("Please select a reason for disagreement.");
            return;
        }
        if (!disagreeNotes.trim()) {
            setDisagreeError("Clinical notes are required.");
            return;
        }
        const authTokens = localStorage.getItem("authTokens");
        let token;
        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            setDisagreeError("Unauthorized. No token available.");
            return;
        }
        const ls = new AccessLocalStorage();
        const userId = ls.getCurrentPatientId();
        setDisagreeing(true);
        setDisagreeError("");
        try {
            const response = await fetch(`${getApiUrl()}/aisummary/updatetriagestatus/${userId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    Id: report.id,
                    TriageReviewStatus: 3,
                    ModificationReason: disagreeReason,
                    AdditionalNotes: disagreeNotes,
                    ModifiedTriageResult: JSON.stringify(disagreeTriageFields),
                }),
            });
            if (!response.ok) {
                const msg = await response.text().catch(() => "An error occurred.");
                setDisagreeError(msg || "An error occurred.");
            } else {
                setDisagreeOpen(false);
                setDisagreeReason("");
                setDisagreeNotes("");
                setDisagreeTriageFields({});
                onReload?.();
            }
        } catch (err) {
            setDisagreeError(err.message || "An error occurred.");
        } finally {
            setDisagreeing(false);
        }
    };

    const handleModifyConfirm = async () => {
        
        if (!modifyReason) {
            setModifyError("Please select a reason for modification.");
            return;
        }
        const authTokens = localStorage.getItem("authTokens");
        let token;
        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            setModifyError("Unauthorized. No token available.");
            return;
        }
        const ls = new AccessLocalStorage();
        const userId = ls.getCurrentPatientId();
        setModifying(true);
        setModifyError("");
        try {
            const response = await fetch(`${getApiUrl()}/aisummary/updatetriagestatus/${userId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    Id: report.id,
                    TriageReviewStatus: 2,
                    ModificationReason: modifyReason,
                    AdditionalNotes: modifyNotes,
                    ModifiedTriageResult: JSON.stringify(modifyTriageFields),
                }),
            });
            if (!response.ok) {
                const msg = await response.text().catch(() => "An error occurred.");
                setModifyError(msg || "An error occurred.");
            } else {
                setModifyOpen(false);
                setModifyReason("");
                setModifyNotes("");
                setModifyTriageFields({});
                onReload?.();
            }
        } catch (err) {
            setModifyError(err.message || "An error occurred.");
        } finally {
            setModifying(false);
        }
    };

    const handleAgree = async () => {
        
        const authTokens = localStorage.getItem("authTokens");
        let token;
        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            setAgreeError("Unauthorized. No token available.");
            return;
        }
        const ls = new AccessLocalStorage();
        const userId = ls.getCurrentPatientId();
        setAgreeing(true);
        setAgreeError("");
        try {
            const response = await fetch(`${getApiUrl()}/aisummary/updatetriagestatus/${userId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    Id: report.id,
                    TriageReviewStatus: 1,
                    ModificationReason: "",
                    AdditionalNotes: "",
                }),
            });
            if (!response.ok) {
                const msg = await response.text().catch(() => "An error occurred.");
                setAgreeError(msg || "An error occurred.");
            } else {
                onReload?.();
            }
        } catch (err) {
            setAgreeError(err.message || "An error occurred.");
        } finally {
            setAgreeing(false);
        }
    };

    const parsed = parseOutput(triage.triageResult);
    const allKeys = getOrderedKeys(null, triage.triageResult);
    const modifiedParsed = ((triage.triageReviewStatus === 2 || triage.triageReviewStatus === 3) && triage.modifiedTriageResult)
        ? parseOutput(triage.modifiedTriageResult)
        : {};

    const isGridKey = (k) => { const lk = k.toLowerCase(); return lk.includes("category") || lk.includes("referral") || lk.includes("severity") || lk.includes("priority") || lk.includes("outcome"); };
    const isFlagsKey = (k) => k.toLowerCase().includes("flag");
    const isReasoningKey = (k) => { const lk = k.toLowerCase(); return lk.includes("reason") || lk.includes("rationale") || lk.includes("explanation") || lk.includes("justif"); };

    const gridKeys = allKeys.filter(isGridKey);
    const flagsKeys = allKeys.filter(isFlagsKey);
    const reasoningKeys = allKeys.filter(isReasoningKey);
    const otherKeys = allKeys.filter(k => !isGridKey(k) && !isFlagsKey(k) && !isReasoningKey(k));

    const GRID_ACCENTS = [
        { color: pcolor, bg: "rgba(17,92,113,0.07)" },
        { color: orangecolor, bg: "rgba(255,145,77,0.10)" },
        { color: "#3DAA6E", bg: "rgba(61,170,110,0.10)" },
        { color: rcolor, bg: lightpinkcolor },
    ];

    return (
        <>
        <Box sx={{
            background: whitecolor, borderRadius: "14px",
            border: "1px solid rgba(133,79,11,0.5)",
            boxShadow: "0 2px 12px rgba(133,79,11,0.07)", overflow: "hidden",
            mb: 3,
        }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}
                sx={{ background: whitecolor, padding: "16px 22px", borderBottom: `1px solid rgba(133,79,11,0.5)` }}
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ background: EMC.goldLight, borderRadius: "8px", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={brownColor} strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                    </Box>
                    <Box>
                        <Typography sx={{  fontWeight: 700,  color: brownColor }}>
                            {triage.triageReviewStatus === 1 ? "Clinician triage" : triage.triageReviewStatus === 2 ? "Clinician triage" :
                                triage.triageReviewStatus === 3 ? "Clinician triage" : "Ariva AI Triage Suggestion" }
                            </Typography>
                        <Typography sx={{ fontFamily: EMC.font, fontSize: "0.72rem", color: brownColor}}>  
                            {dayjs(report.createdAt).format("DD/MM/YYYY")}
                            {triage.modelName ? ` · ${triage.modelName}` : ""}
                        </Typography>
                    </Box>
                </Box>
                {triage.triageReviewStatus != null && (
                    <Box sx={{ background: EMC.goldLight, borderRadius: "20px", border: `1px solid ${brownColor}`, padding: "3px 12px" }}>
                        <Typography sx={{  fontWeight: 700, color: brownColor }}>
                            {({ 0: "Not Reviewed", 1: "Confirmed", 2: "Amended", 3: "Overridden" }[triage.triageReviewStatus] ?? triage.triageReviewStatus)}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Body */}
            <Box sx={{ padding: "20px 24px" }}>
                {/* Category / Severity grid */}
                {gridKeys.length > 0 && (
                    <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                        {gridKeys.map((key, i) => (
                            <TriageGridCard
                                key={key}
                                fieldKey={key}
                                value={parsed[key]}
                                accent={GRID_ACCENTS[i % GRID_ACCENTS.length]}
                                modifiedValue={(triage.triageReviewStatus === 2 || triage.triageReviewStatus === 3) ? valueToEditString(modifiedParsed[key] ?? parsed[key]) : undefined}
                                isModifiedStatus={triage.triageReviewStatus === 2 || triage.triageReviewStatus === 3}
                            />
                        ))}
                    </Box>
                )}

                {/* Flags */}
                {flagsKeys.map((key) => {
                    const val = parsed[key];
                    const items = Array.isArray(val) ? val : typeof val === "string" ? [val] : [];
                    if (!items.length) return null;
                    return (
                        <Box key={key} mb={3} mt={3}>
                            <Typography sx={{  color: primaryColor, textTransform: "uppercase", fontWeight: 700, mb: 0.7 }}>
                                {humanize(key)}
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.7}>
                                {items.map((f, i) => (
                                    <Box key={i} component="div" sx={{
                                        background: lightpinkcolor, color: rcolor,
                                        border: `1px solid ${rcolor}33`,
                                        borderRadius: "20px", padding: "4px 12px",
                                        
                                    }}>⚠ {f}</Box>
                                ))}
                            </Box>
                        </Box>
                    );
                })}

                {/* Reasoning */}
                {reasoningKeys.map((key) => {
                    const val = parsed[key];
                    if (!val) return null;
                    const text = typeof val === "string" ? val : JSON.stringify(val);
                    return (
                        <Box key={key} mb={2} sx={{ background: EMC.navyLight, borderRadius: "8px", padding: "12px 16px", border: `1px solid ${EMC.border}` }}>
                            <Typography sx={{ color: primaryColor, textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.6, fontWeight:"bold" }}>
                                {humanize(key)}
                            </Typography>
                            <Typography sx={{ color: EMC.textPrimary, lineHeight: 1.75 }}>{text}</Typography>
                        </Box>
                    );
                })}

                {/* Other fields */}
                {otherKeys.map((key, i) => (
                    <FullField key={key} fieldKey={key} value={parsed[key]} isFirst={i === 0 && gridKeys.length === 0 && flagsKeys.length === 0 && reasoningKeys.length === 0} />
                ))}

                {/* Agreed locked state — status 1 */}
                {triage.triageReviewStatus === 1 && (
                    <Box sx={{ background: "#eaf3de", border: "1px solid #c0dd97", borderRadius: "8px", padding: "10px 13px", display: "flex", alignItems: "flex-start", gap: 1.5, mt: 2 }}>
                        <Box sx={{ width: 22, height: 22, borderRadius: "50%", background: "#1a7a6e", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: "1px" }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily: EMC.font, fontWeight: 600, fontSize: "0.82rem", color: "#27500a", mb: 0.3 }}>
                                Clinician triage — Confirmed
                            </Typography>
                            <Typography sx={{ fontFamily: EMC.font, fontSize: "0.75rem", color: "#3b6d11" }}>
                                Agreed by {triage.triageReviewedByName ?? "clinician"}
                                {triage.triageReviewedAt ? ` · ${dayjs(triage.triageReviewedAt).format("DD/MM/YYYY [at] HH:mm")}` : ""}
                                {" · Family and referrer notified automatically."}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Modified locked state — status 2 */}
                {triage.triageReviewStatus === 2 && (
                    <Box sx={{ background: "#eaf3de", border: "1px solid #c0dd97", borderRadius: "8px", padding: "10px 13px", display: "flex", alignItems: "flex-start", gap: 1.5, mt: 2 }}>
                        <Box sx={{ width: 22, height: 22, borderRadius: "50%", background: "#1a7a6e", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: "1px" }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily: EMC.font, fontWeight: 600, fontSize: "0.82rem", color: "#27500a", mb: 0.3 }}>
                                Clinician triage — Amended
                            </Typography>
                            <Typography sx={{ fontFamily: EMC.font, fontSize: "0.75rem", color: "#3b6d11" }}>
                                Modified by {triage.triageReviewedByName ?? "clinician"}
                                {triage.triageReviewedAt ? ` · ${dayjs(triage.triageReviewedAt).format("DD/MM/YYYY [at] HH:mm")}` : ""}
                                {" · Family and referrer notified automatically."}
                            </Typography>
                            {triage.modificationReason && (
                                <Typography sx={{ fontFamily: EMC.font, fontSize: "0.72rem", color: "#3b6d11", mt: 0.5 }}>
                                    Reason: {triage.modificationReason}{triage.additionalNotes ? ` · ${triage.additionalNotes}` : ""}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Disagreed locked state — status 3 */}
                {triage.triageReviewStatus === 3 && (
                    <Box sx={{ background: whitecolor, border: `1px solid ${rcolor}44`, borderRadius: "8px", padding: "10px 13px", display: "flex", alignItems: "flex-start", gap: 1.5, mt: 2 }}>
                        <Box sx={{ width: 22, height: 22, borderRadius: "50%", background: rcolor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: "1px" }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 600, color: rcolor, mb: 0.3 }}>
                                Clinician triage — Overridden
                            </Typography>
                            <Typography sx={{  color: rcolor }}>
                                Overridden by {triage.triageReviewedByName ?? "clinician"}
                                {triage.triageReviewedAt ? ` · ${dayjs(triage.triageReviewedAt).format("DD/MM/YYYY [at] HH:mm")}` : ""}
                                {" · Family and referrer notified automatically."}
                            </Typography>
                            {triage.modificationReason && (
                                <Typography sx={{ color: rcolor, mt: 0.5 }}>
                                    Reason: {triage.modificationReason}{triage.additionalNotes ? ` · Notes: ${triage.additionalNotes}` : ""}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Review note + Actions — only shown when status is 0 (Not Reviewed) */}
                {triage.triageReviewStatus === 0 && (
                    <>
                        <Typography sx={{ fontSize: "0.75rem", color: EMC.textSecondary, mt: 2.5, mb: 1.5, lineHeight: 1.6 }}>
                            Review the suggested triage below. You can confirm the suggestion, amend individual components, or replace both with your own decision. <strong>Your decision — not the AI suggestion — is what is recorded and acted upon.</strong> This action is final once confirmed.
                        </Typography>

                        <Box display="flex" gap={1.5} flexWrap="wrap" my={2}>
                            <Button
                                variant="contained"
                                size="small"
                                disabled={agreeing}
                                onClick={handleAgree}
                                sx={{ fontWeight: 700, textTransform: "none", background: "#3DAA6E", color: "#fff", borderRadius: "20px", padding: "6px 20px", boxShadow: "none", "&:hover": { background: "#2d8a56", boxShadow: "none" } }}
                            >
                                {agreeing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Confirm Suggestion"}
                            </Button>
                            <Button variant="outlined" size="small" 
                            onClick={() => { setModifyTriageFields(initTriageFields()); setModifyOpen(true); setModifyError(""); }} sx={{ fontFamily: EMC.font, fontWeight: 700, textTransform: "none", border: `1px solid ${pcolor}`, color: pcolor, borderRadius: "20px", padding: "6px 20px", "&:hover": { background: "rgba(17,92,113,0.06)" } }}>Amend</Button>
                            <Button variant="outlined" size="small" onClick={() => { setDisagreeTriageFields(initTriageFields()); setDisagreeOpen(true); setDisagreeError(""); }} sx={{ fontFamily: EMC.font, fontWeight: 700, textTransform: "none", border: `1px solid ${rcolor}`, color: rcolor, borderRadius: "20px", padding: "6px 20px", "&:hover": { background: lightpinkcolor } }}>Replace Entirely</Button>
                        </Box>
                        {agreeError && (
                            <Typography sx={{ fontSize: "0.75rem", color: rcolor, mt: 0.5 }}>
                                {agreeError}
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: "0.7rem", color: EMC.textSecondary }}>
                            Confirm accepts the suggestion as-is. Amend lets you change individual components. Replace substitutes both components with your own clinical decision. All actions are recorded with your name, role, and timestamp.
                        </Typography>
                    </>
                )}
            </Box>
        </Box>

        {/* Modify modal */}
        <Dialog open={modifyOpen} onClose={() => setModifyOpen(false)} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
        >
            <DialogTitle sx={{ background: pcolor, padding: "13px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontFamily: EMC.font, fontWeight: 600, fontSize: "0.9rem", color: whitecolor }}>Modify triage decision</Typography>
                <IconButton size="small" onClick={() => setModifyOpen(false)}
                    sx={{ color: whitecolor, border: "1.5px solid rgba(255,255,255,0.4)", borderRadius: "50%", width: 26, height: 26 }}
                >
                    <Typography sx={{ fontSize: "0.8rem", lineHeight: 1 }}>✕</Typography>
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "16px 18px", mt: 1 }}>
                <Box sx={{ background: EMC.navyLight, borderLeft: `3px solid ${pcolor}`, borderRadius: "0 8px 8px 0", padding: "10px 12px", mb: 2, fontSize: "0.78rem", color: pcolor, lineHeight: 1.5, fontFamily: EMC.font }}>
                    Review each triage component and change only the ones you disagree with. Unchanged components will be recorded as agreed. This action is final once confirmed.
                </Box>

                {/* Triage fields */}
                <Typography sx={{ fontFamily: EMC.font, fontWeight: 700,  textTransform: "uppercase", letterSpacing: "0.07em", color: darkcolor, mb: 2 }}>Triage fields</Typography>
                {Object.keys(modifyTriageFields).map((key) => (
                    <TextField
                        key={key}
                        label={humanize(key)}
                        fullWidth
                        multiline
                        value={modifyTriageFields[key]}
                        onChange={(e) => setModifyTriageFields(prev => ({ ...prev, [key]: e.target.value.slice(0, 500) }))}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${modifyTriageFields[key].length}/500`}
                        sx={{
                            mb: 2,
                            "& .MuiInputBase-root": { borderRadius: "8px", fontFamily: EMC.font },
                            "& label": { fontFamily: EMC.font,  color: pcolor },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: `${pcolor}44` },
                            "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: pcolor },
                            "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: pcolor },
                            "& .MuiFormHelperText-root": { color: darkcolor, fontFamily: EMC.font },
                        }}
                    />
                ))}

                <FormControl fullWidth required sx={{ mb: 2 }}>
                    <InputLabel sx={{ fontFamily: EMC.font, color: pcolor }}>Reason for modification</InputLabel>
                    <Select
                        value={modifyReason}
                        label="Reason for modification"
                        onChange={(e) => setModifyReason(e.target.value)}
                        sx={{
                            fontFamily: EMC.font, borderRadius: "8px",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: `${pcolor}66` },
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: pcolor },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: pcolor },
                        }}
                    >
                        {MODIFY_REASONS.map((r) => (
                            <MenuItem key={r} value={r} sx={{ fontFamily: EMC.font }}>{r}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Additional notes (optional)"
                    multiline
                    minRows={3}
                    fullWidth
                    value={modifyNotes}
                    onChange={(e) => setModifyNotes(e.target.value.slice(0, 1000))}
                    placeholder="Add any clinical context that informed this modification..."
                    inputProps={{ maxLength: 1000 }}
                    helperText={`${modifyNotes.length}/1000`}
                    sx={{
                        "& .MuiInputBase-root": { borderRadius: "8px", fontFamily: EMC.font },
                        "& label": { fontFamily: EMC.font, color: pcolor },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: `${pcolor}66` },
                        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: pcolor },
                        "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: pcolor },
                        "& .MuiFormHelperText-root": { color: darkcolor, fontFamily: EMC.font },
                    }}
                />

                {modifyError && (
                    <Typography sx={{ fontFamily: EMC.font, fontSize: "0.75rem", color: rcolor, mt: 2 }}>{modifyError}</Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: "12px 18px", borderTop: `0.5px solid ${lightcolor}`, gap: 1 }}>
                <Button onClick={() => setModifyOpen(false)}
                    sx={{ fontFamily: EMC.font, fontSize: "0.78rem", textTransform: "none", color: darkcolor, border: `0.5px solid ${lightcolor}`, borderRadius: "20px", px: 3 }}
                >
                    Cancel
                </Button>
                <Button variant="contained" disabled={modifying} onClick={handleModifyConfirm}
                    sx={{ fontFamily: EMC.font, fontWeight: 600, fontSize: "0.78rem", textTransform: "none", background: pcolor, borderRadius: "20px", px: 3, boxShadow: "none", "&:hover": { background: primaryColor, boxShadow: "none", opacity: 0.85 } }}
                >
                    {modifying ? <CircularProgress size={16} sx={{ color: whitecolor }} /> : "Confirm modification"}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Disagree modal */}
        <Dialog open={disagreeOpen} onClose={() => setDisagreeOpen(false)} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
        >
            <DialogTitle sx={{ background: brownColor, padding: "13px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontFamily: EMC.font, fontWeight: 600, color: whitecolor }}>Disagree with triage decision</Typography>
                <IconButton size="small" onClick={() => setDisagreeOpen(false)}
                    sx={{ color: whitecolor, border: "1.5px solid rgba(255,255,255,0.4)", borderRadius: "50%", width: 26, height: 26 }}
                >
                    <Typography sx={{ fontSize: "0.8rem", lineHeight: 1 }}>✕</Typography>
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "16px 18px", mt: 1 }}>
                <Box sx={{ background: EMC.goldLight, borderLeft: `3px solid ${brownColor}`, borderRadius: "0 8px 8px 0", padding: "10px 12px", mb: 2, fontSize: "0.78rem", color: brownColor, lineHeight: 1.5, fontFamily: EMC.font }}>
                    You are replacing the AI triage decision entirely. Both components must be reassigned. This action is final — once confirmed the downstream letter is sent to the family and referrer automatically with your decision. Your name and timestamp will be recorded.
                </Box>

                {/* AI decision being replaced */}
                <Box sx={{ background: lightcolor, borderRadius: "8px", padding: "9px 12px", mb: 2 }}>
                    <Typography sx={{ fontFamily: EMC.font,  fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: darkcolor, mb: 1}}>AI decision being replaced</Typography>
                    <Box display="flex" gap={3} flexWrap="wrap">
                        {Object.entries(parseOutput(triage.triageResult))
                            .filter(([k]) => k.toLowerCase().includes("category") || k.toLowerCase().includes("referral") || k.toLowerCase().includes("severity") || k.toLowerCase().includes("priority"))
                            .map(([k, v]) => (
                                <Box key={k}>
                                    <Typography sx={{ fontFamily: EMC.font, color: darkcolor, mb: 0.2 }}>{humanize(k)}</Typography>
                                    <Typography sx={{ fontFamily: EMC.font, color: darkcolor, textDecoration: "line-through" }}>
                                        {typeof v === "object" && v !== null && v.value ? v.value : typeof v === "string" ? v : JSON.stringify(v)}
                                    </Typography>
                                </Box>
                            ))
                        }
                    </Box>
                </Box>

                {/* Triage fields */}
                <Typography sx={{ fontFamily: EMC.font, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: darkcolor, mb: 1 }}>Triage fields</Typography>
                {Object.keys(disagreeTriageFields).map((key) => (
                    <TextField
                        key={key}
                        label={humanize(key)}
                        fullWidth
                        multiline
                        value={disagreeTriageFields[key]}
                        onChange={(e) => setDisagreeTriageFields(prev => ({ ...prev, [key]: e.target.value.slice(0, 500) }))}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${disagreeTriageFields[key].length}/500`}
                        sx={{
                            mb: 1.5,
                            "& .MuiInputBase-root": { borderRadius: "8px", fontFamily: EMC.font },
                            "& label": { fontFamily: EMC.font, color: rcolor },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: `${rcolor}44` },
                            "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: rcolor },
                            "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: rcolor },
                            "& .MuiFormHelperText-root": { color: darkcolor, fontFamily: EMC.font },
                        }}
                    />
                ))}

                <FormControl fullWidth required sx={{ mb: 2 }}>
                    <InputLabel sx={{ fontFamily: EMC.font, color: rcolor }}>Reason for disagreement</InputLabel>
                    <Select
                        value={disagreeReason}
                        label="Reason for disagreement"
                        onChange={(e) => setDisagreeReason(e.target.value)}
                        sx={{
                            fontFamily: EMC.font, borderRadius: "8px",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: `${rcolor}66` },
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: rcolor },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: rcolor },
                        }}
                    >
                        {DISAGREE_REASONS.map((r) => (
                            <MenuItem key={r} value={r} sx={{ fontFamily: EMC.font }}>{r}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Clinical notes"
                    required
                    multiline
                    minRows={3}
                    fullWidth
                    value={disagreeNotes}
                    onChange={(e) => setDisagreeNotes(e.target.value.slice(0, 1000))}
                    placeholder="Explain the clinical reasoning for this disagreement..."
                    inputProps={{ maxLength: 1000 }}
                    helperText={`${disagreeNotes.length}/1000`}
                    sx={{
                        "& .MuiInputBase-root": { borderRadius: "8px", fontFamily: EMC.font },
                        "& label": { fontFamily: EMC.font, color: rcolor },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: `${rcolor}66` },
                        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: rcolor },
                        "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: rcolor },
                        "& .MuiFormHelperText-root": { color: darkcolor, fontFamily: EMC.font },
                    }}
                />

                {disagreeError && (
                    <Typography sx={{ fontFamily: EMC.font, fontSize: "0.75rem", color: rcolor, mt: 1 }}>{disagreeError}</Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: "12px 18px", borderTop: `0.5px solid ${lightcolor}`, gap: 1 }}>
                <Button onClick={() => setDisagreeOpen(false)}
                    sx={{ fontFamily: EMC.font, fontSize: "0.78rem", textTransform: "none", color: darkcolor, border: `0.5px solid ${lightcolor}`, borderRadius: "20px", px: 3 }}
                >
                    Cancel
                </Button>
                <Button variant="contained" disabled={disagreeing} onClick={handleDisagreeConfirm}
                    sx={{ fontFamily: EMC.font, fontWeight: 600, fontSize: "0.78rem", textTransform: "none", background: rcolor, borderRadius: "20px", px: 3, boxShadow: "none", "&:hover": { background: rcolor, boxShadow: "none", opacity: 0.85 } }}
                >
                    {disagreeing ? <CircularProgress size={16} sx={{ color: whitecolor }} /> : "Confirm disagreement"}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}

const AISummaryDashboard = () => {
    const { pathway, organizationId } = useParams();
    const auth = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState([]);

    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState("");
    const [selectedHistory, setSelectedHistory] = useState(null);

    const ls = new AccessLocalStorage();

    const loadSummary = useCallback(() => {
        const authTokens = localStorage.getItem("authTokens");
        let token;
        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            setError("Unauthorized. No token available.");
            return;
        }

        setLoading(true);

        // Build the API URL, append pathwayId and organizationId if provided
        let apiUrl = `${getApiUrl()}/AISummary/${ls.getCurrentPatientId()}`;
        if (pathway) {
            const sep = apiUrl.includes("?") ? "&" : "?";
            apiUrl += `${sep}p=${encodeURIComponent(pathway)}`;
        }
        if (organizationId) {
            const sep = apiUrl.includes("?") ? "&" : "?";
            apiUrl += `${sep}o=${encodeURIComponent(organizationId)}`;
        }

        fetch(apiUrl, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => {
                if (response.status === 401) {
                    auth.logoutUser();
                    return;
                }
                if (response.status === 200) {
                    response.json().then((data) => {
                        //console.log("AI Summary response data:", data);
                        if (data !== null) {
                            setResult(Array.isArray(data) ? data : [data]);
                        }
                    });
                } else {
                    setResult([]);
                    response.json()
                        .then(() => setError("An error occurred."))
                        .catch((err) => { console.log(err.message); setError("No published summary available."); });
                }
            })
            .catch((err) => { console.log(err.message); setError("No published summary available."); })
            .finally(() => setLoading(false));
    }, [pathway, organizationId]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    useEffect(() => {
        const authTokens = localStorage.getItem("authTokens");
        let token;
        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            setError("Unauthorized. No token available.");
            return;
        }

        setLoading(true);

        // Build the API URL, append pathwayId if provided
        let apiUrl = `${getApiUrl()}/AISummary/${ls.getCurrentPatientId()}/history`;
        if (pathway) {
            const sep = apiUrl.includes("?") ? "&" : "?";
            apiUrl += `${sep}p=${encodeURIComponent(pathway)}`;
        }
        if (organizationId) {
            const sep = apiUrl.includes("?") ? "&" : "?";
            apiUrl += `${sep}o=${encodeURIComponent(organizationId)}`;
        }

        fetch(apiUrl, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => {
                if (response.status === 401) {
                    auth.logoutUser();
                    return;
                }
                if (response.status === 200) {
                    response.json().then((data) => {
                        console.log("AI Summary history response data:", data);
                        if (data !== null) {
                            setHistory(Array.isArray(data) ? data : [data]);
                        }
                    });
                } else {
                    setHistory([]);
                    response.json()
                        .then(() => setHistoryError("An error occurred."))
                        .catch((err) => setHistoryError(err.message || "An error occurred."));
                }
            })
            .catch((err) => setHistoryError(err.message || "An error occurred."))
            .finally(() => setHistoryLoading(false));
    }, [pathway, organizationId]);

    return (
        <Box sx={{ bgcolor: "#f2eee4", minWidth: "1200px" }}>
            <CssBaseline />
            <MetaDecorator title={"EnrichMyCare - Patient Profile"} description={"Professional dashboard app"} />
            <Newheader tab_disabled={true} show_profile_button={true} show_back_button={true} fullWidth={true} />

            <Box component="div" sx={{ ...dashboardText, textAlign: "center", fontWeight: "bold", marginTop: "2rem", marginBottom: "2rem" }}>
                AI Dashboard
            </Box>

            <Dashboard fullWidth={true} profile_image={true} detail_text={true} share_icons={false} end_card={true} cards={true} health_profile_button={true} />
            <Box m={1} />

            {/* AI Summary Tabs */}
            <Box sx={{ maxWidth: "1200px", margin: "0 auto", px: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        borderBottom: `1px solid ${lightcolor}`,
                        mb: 2,
                        "& .MuiTab-root": {
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            textTransform: "none",
                            color: darkcolor,
                            minWidth: 110,
                        },
                        "& .Mui-selected": { color: pcolor },
                        "& .MuiTabs-indicator": { backgroundColor: pcolor, height: 3, borderRadius: 2 },
                    }}
                >
                    <Tab label="Current" />
                    <Tab label="History" />
                </Tabs>

                {activeTab === 0 && (
                    <Box sx={{ paddingTop: "15px", minHeight: result.length === 0 ? "400px" : undefined }}>
                        {loading && <CircularProgress size={24} sx={{ color: pcolor, ml: 1 }} />}
                        {error && (
                            <Box sx={{ background: "#FAE6EC", border: "1px solid #FF767E44", borderRadius: "10px", padding: "12px 18px", mb: 2 }}>
                                <Typography sx={{ fontSize: "0.85rem", color: "#FF767E", fontWeight: 600 }}>{error}</Typography>
                            </Box>
                        )}
                        {!loading && result.length === 0 && !error && (
                            <Typography sx={{ color: darkcolor, textAlign: "center", mt: 4 }}>No summary available for this patient.</Typography>
                        )}
                        {result.map((report) => (
                            <Box key={report.id}>
                                <FullReportCard report={report} />
                                {report.triageDetails?.triageEnabled && (
                                    <TriagePanel report={report} onReload={loadSummary} />
                                )}
                                <Box mb={2} />

                                {/* ── Data Sources Section ───────────────────────────────────────────────
                                     The components below show the actual patient data that was provided
                                     to the AI when generating the summary above. These records cover the
                                     date range and selections configured for this summary run, giving
                                     full transparency into the inputs the AI used.
                                ─────────────────────────────────────────────────────────────────────── */}
                                {(
                                    (report.formIdList && report.formIdList.length > 0) ||
                                    (report.seizureDiaryEnabled && report.seizureDiaryStartDate !== null && report.seizureDiaryEndDate !== null) ||
                                    (report.sleepDiaryEnabled && report.sleepDiaryStartDate !== null && report.sleepDiaryEndDate !== null) ||
                                    (report.journalEnabled && report.journalStartDate !== null && report.journalEndDate !== null)
                                ) && (
                                    <Box mb={2} mt={1}>
                                        <Typography sx={redHeading}>
                                            Data Sources Used to Generate This Summary
                                        </Typography>
                                        <Typography sx={{
                                            fontFamily: EMC.font, fontSize: "1rem",
                                            color: darkcolor, 
                                        }}>
                                            The records below were provided to the AI as input for this summary. They reflect the date range and selections configured at the time of generation, offering full transparency into the data the AI analysed.
                                        </Typography>
                                    </Box>
                                )}

                                {report.formIdList && report.formIdList.length > 0 && (
                                    <Box mb={3}>
                                        <FormsList icon={`../../assets/forms.png`} id="formslist" fullWidth={true} showOnly={report.formIdList} />
                                    </Box>
                                )}
                                {report.seizureDiaryEnabled && report.seizureDiaryStartDate !== null && report.seizureDiaryEndDate !== null && (
                                    <Box mb={3}>
                                        <SeizureDiary icon={`../../assets/epilepsy.png`} id="seizurediary" fullWidth={true} startDate={report.seizureDiaryStartDate} endDate={report.seizureDiaryEndDate} />
                                    </Box>
                                )}
                                {report.sleepDiaryEnabled && report.sleepDiaryStartDate !== null && report.sleepDiaryEndDate !== null && (
                                    <Box mb={3}>
                                        <SleepDiary icon={`../../assets/sleep-diary.png`} id="sleepdiary" fullWidth={true} startDate={report.sleepDiaryStartDate} endDate={report.sleepDiaryEndDate} />
                                    </Box>
                                )}
                                {report.journalEnabled && report.journalStartDate !== null && report.journalEndDate !== null && (
                                    <Box mb={3}>
                                        <HealthDiary icon={`../../assets/diary.png`} id="healthdiary" fullWidth={true} startDate={report.journalStartDate} endDate={report.journalEndDate} newJournalCreatedTime={null} />
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                {activeTab === 1 && (
                    <Box sx={{ paddingTop: "15px", minHeight: history.length === 0 ? "400px" : undefined }}>
                        {historyLoading && <CircularProgress size={24} sx={{ color: pcolor, ml: 1 }} />}
                        {historyError && (
                            <Box sx={{ background: lightpinkcolor, border: `1px solid ${rcolor}44`, borderRadius: "10px", padding: "12px 18px", mb: 2 }}>
                                <Typography sx={{ fontSize: "0.85rem", color: rcolor, fontWeight: 600 }}>{historyError}</Typography>
                            </Box>
                        )}
                        {!historyLoading && history.length === 0 && !historyError && (
                            <Typography sx={{ color: darkcolor, textAlign: "center", mt: 4 }}>No history available for this patient.</Typography>
                        )}

                        {/* History list */}
                        {!selectedHistory && history.length > 0 && (
                            <Box sx={{ background: whitecolor, border: `0.5px solid ${EMC.border}`, borderRadius: "12px", padding: "3px 14px" }}>
                                {history.map((report) => (
                                    <HistoryRow key={report.id} report={report} onView={(r) => setSelectedHistory(r)} />
                                ))}
                            </Box>
                        )}

                        {/* Detail view for selected history item */}
                        {selectedHistory && (
                            <Box>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <Button size="small" variant="outlined" onClick={() => setSelectedHistory(null)}
                                        sx={{ fontFamily: EMC.font, fontWeight: 600, fontSize: "0.78rem", textTransform: "none", color: pcolor, border: `1px solid ${pcolor}44`, borderRadius: "8px", "&:hover": { background: EMC.navyLight } }}>
                                        ← Back to history
                                    </Button>
                                    <Typography sx={{ fontFamily: EMC.font, fontSize: "0.82rem", color: EMC.textSecondary }}>
                                        {dayjs(selectedHistory.createdAt).format("DD/MM/YYYY [at] HH:mm")}
                                    </Typography>
                                </Box>
                                <FullReportCard report={selectedHistory} />
                                {selectedHistory.triageDetails?.triageEnabled && (
                                    <TriagePanel report={selectedHistory} onReload={loadSummary} />
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            <BackToTop />
            <InactivityMeter />
            <Box sx={{ ...footerContainer, position: "static", marginTop: "20px" }}>
                <Footer />
            </Box>
        </Box>
    );
};

export default AISummaryDashboard;